/**
 * Track manipulation effects — non-destructive overlays applied at generation time.
 *
 * Each manipulation targets a segment of a track (by waypoint index range)
 * and applies a post-processing effect to the generated positions within that range.
 */

// ---------------------------------------------------------------------------
// Base
// ---------------------------------------------------------------------------

export interface ManipulationBase {
  /** Unique identifier for this manipulation (used as PRNG seed) */
  id: string;
  /** Whether this manipulation is currently active */
  enabled: boolean;
  /** Start waypoint index (inclusive) — maps to a fraction of track duration */
  startIndex: number;
  /** End waypoint index (inclusive) — maps to a fraction of track duration */
  endIndex: number;
  /** Fraction of total track duration where this manipulation starts [0..1] */
  startFraction: number;
  /** Fraction of total track duration where this manipulation ends [0..1] */
  endFraction: number;
}

// ---------------------------------------------------------------------------
// Signal Gap (dark period — vessel stops transmitting)
// ---------------------------------------------------------------------------

export interface SignalGap extends ManipulationBase {
  type: 'signal_gap';
}

// ---------------------------------------------------------------------------
// GPS Spoofing (positions gradually shift to a false location)
// ---------------------------------------------------------------------------

export interface GpsSpoofing extends ManipulationBase {
  type: 'gps_spoofing';
  config: {
    /** Maximum latitude offset in degrees at peak spoofing */
    maxOffsetLat: number;
    /** Maximum longitude offset in degrees at peak spoofing */
    maxOffsetLon: number;
    /** Fraction of the manipulation range used for smooth transition in [0..0.5] */
    transitionFraction: number;
  };
}

// ---------------------------------------------------------------------------
// GPS Jamming (noisy/scattered positions + occasional drops)
// ---------------------------------------------------------------------------

export interface GpsJamming extends ManipulationBase {
  type: 'gps_jamming';
  config: {
    /** Maximum random noise in degrees added to lat/lon */
    noiseDeg: number;
    /** Fraction of positions to drop (0..1) */
    dropRate: number;
  };
}

// ---------------------------------------------------------------------------
// Position Outliers (random large jumps at scattered points)
// ---------------------------------------------------------------------------

export interface PositionOutliers extends ManipulationBase {
  type: 'position_outliers';
  config: {
    /** Number of outlier spikes to inject */
    count: number;
    /** Maximum jump distance in degrees */
    maxJumpDeg: number;
  };
}

// ---------------------------------------------------------------------------
// Union & helpers
// ---------------------------------------------------------------------------

export type TrackManipulation =
  | SignalGap
  | GpsSpoofing
  | GpsJamming
  | PositionOutliers;

export type ManipulationType = TrackManipulation['type'];

export const MANIPULATION_LABELS: Record<ManipulationType, string> = {
  signal_gap: 'Signal Gap',
  gps_spoofing: 'GPS Spoofing',
  gps_jamming: 'GPS Jamming',
  position_outliers: 'Position Outliers',
};

export const MANIPULATION_DESCRIPTIONS: Record<ManipulationType, string> = {
  signal_gap: 'Vessel goes dark — no transmissions for a period',
  gps_spoofing: 'Position gradually drifts to a fake location',
  gps_jamming: 'Noisy/scattered positions + dropped reports',
  position_outliers: 'Random position jumps/spikes in the track',
};

/** Longer explanations describing what each effect does to the track data. */
export const MANIPULATION_DETAILS: Record<ManipulationType, string> = {
  signal_gap:
    'Simulates a vessel turning off its AIS transponder. ' +
    'Positions in the selected range are kept but stripped of all data except timestamp and MMSI — ' +
    'coordinates, SOG, COG, dimensions, and all other fields are set to "not available" values. ' +
    'The vessel effectively disappears from the map while still technically transmitting.',
  gps_spoofing:
    'Positions are gradually shifted away from the real track toward a false location. ' +
    'The drift ramps up smoothly, holds at the maximum offset, then ramps back down — ' +
    'so the vessel appears to sail to a wrong location and eventually return. ' +
    'Spoofed positions are tagged so downstream systems can identify the manipulation.',
  gps_jamming:
    'Simulates a jammed GPS receiver. A percentage of AIS messages are randomly dropped (lost), ' +
    'and the surviving positions get random noise added to their coordinates — ' +
    'making the track look scattered and erratic on the map. ' +
    'When the track has very few positions in the range, the drop rate is automatically reduced ' +
    'to avoid removing nearly everything.',
  position_outliers:
    'A set number of random positions within the range are given large coordinate jumps — ' +
    'they appear as sudden spikes far from the real track, then snap back. ' +
    'This simulates sensor glitches or brief GPS errors. ' +
    'Outlier positions are tagged with an anomaly label so downstream systems can ' +
    'identify them and avoid bridging fake positions into and out of the spike.',
};

// ---------------------------------------------------------------------------
// Unit conversion helpers (degrees ↔ meters)
// ---------------------------------------------------------------------------

const DEG_PER_M = 1 / 111_320; // 1 m ≈ 1/111320° at equator

/** Convert meters to degrees (latitude approximation) */
export function metersToDeg(m: number): number {
  return m * DEG_PER_M;
}

/** Convert degrees to meters (latitude approximation) */
export function degToMeters(deg: number): number {
  return deg / DEG_PER_M;
}

// ---------------------------------------------------------------------------
// Presets — pre-configured effect templates
// ---------------------------------------------------------------------------

export interface ManipulationPreset {
  label: string;
  type: ManipulationType;
  description: string;
  config?: Record<string, unknown>;
}

export const MANIPULATION_PRESETS: ManipulationPreset[] = [
  {
    label: 'AIS gap (10 min)',
    type: 'signal_gap',
    description: 'Realistic signal loss — ~10 min dark period',
  },
  {
    label: 'Slow drift (~500 m)',
    type: 'gps_spoofing',
    description: 'Gradual position drift up to ~500 m',
    config: {
      maxOffsetLat: metersToDeg(500),
      maxOffsetLon: metersToDeg(500),
      transitionFraction: 0.25,
    },
  },
  {
    label: 'Large spoof (~5 km)',
    type: 'gps_spoofing',
    description: 'Aggressive spoofing — shifts position ~5 km',
    config: {
      maxOffsetLat: metersToDeg(5000),
      maxOffsetLon: metersToDeg(5000),
      transitionFraction: 0.15,
    },
  },
  {
    label: 'Heavy jamming',
    type: 'gps_jamming',
    description: '200 m noise + 40% message drops',
    config: { noiseDeg: metersToDeg(200), dropRate: 0.4 },
  },
  {
    label: 'Light jamming',
    type: 'gps_jamming',
    description: '50 m noise + 15% drops',
    config: { noiseDeg: metersToDeg(50), dropRate: 0.15 },
  },
  {
    label: '3 position spikes',
    type: 'position_outliers',
    description: 'Inject 3 random jumps (~3 km each)',
    config: { count: 3, maxJumpDeg: metersToDeg(3000) },
  },
];

/** Default configs for each manipulation type */
export function createDefaultManipulation(
  type: ManipulationType,
  id: string,
  startIndex: number,
  endIndex: number,
  startFraction: number,
  endFraction: number,
  presetConfig?: Record<string, unknown>,
): TrackManipulation {
  const base: ManipulationBase = {
    id,
    enabled: true,
    startIndex,
    endIndex,
    startFraction,
    endFraction,
  };

  switch (type) {
    case 'signal_gap':
      return { ...base, type: 'signal_gap' };
    case 'gps_spoofing': {
      const defaults: GpsSpoofing['config'] = {
        maxOffsetLat: 0.05,
        maxOffsetLon: 0.05,
        transitionFraction: 0.2,
      };
      return {
        ...base,
        type: 'gps_spoofing',
        config: { ...defaults, ...presetConfig } as GpsSpoofing['config'],
      };
    }
    case 'gps_jamming': {
      const defaults: GpsJamming['config'] = {
        noiseDeg: 0.01,
        dropRate: 0.3,
      };
      return {
        ...base,
        type: 'gps_jamming',
        config: { ...defaults, ...presetConfig } as GpsJamming['config'],
      };
    }
    case 'position_outliers': {
      const defaults: PositionOutliers['config'] = {
        count: 3,
        maxJumpDeg: 0.5,
      };
      return {
        ...base,
        type: 'position_outliers',
        config: { ...defaults, ...presetConfig } as PositionOutliers['config'],
      };
    }
  }
}
